import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = React.memo(() => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const handleToggleTheme = useCallback(() => {
    toggleTheme();
  }, [toggleTheme]);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleToggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    // { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        when: "afterChildren"
      }
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.4,
        ease: "easeOut",
        when: "beforeChildren",
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    closed: { opacity: 0, x: -10 },
    open: { opacity: 1, x: 0 }
  };

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${scrolled || isMenuOpen
          ? 'bg-white/80 dark:bg-gray-950/80 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-800/50 shadow-sm'
          : 'bg-white dark:bg-gray-950 border-b border-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 transition-all duration-300">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
            onClick={handleCloseMenu}
          >
            <div className="bg-white dark:bg-black text-white dark:text-black p-2 rounded-lg group-hover:bg-gray-300 dark:group-hover:bg-gray-800 transition-colors">
              <div>
                {/* Light mode image */}
                <img
                  src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/logo/projectlockicon.PNG"
                  alt="logo"
                  className="w-8 h-8 hidden dark:block"
                />

                {/* Dark mode image */}
                <img
                  src="https://universal-service.sfo3.cdn.digitaloceanspaces.com/logo/projectlockicon.PNG"
                  alt="logo dark"
                  className="w-8 h-8 block dark:hidden"
                />
              </div>
            </div>

            <span className="font-bold text-lg tracking-tight text-gray-900 dark:text-white group-hover:text-black dark:group-hover:text-gray-200 transition-colors">
              Project Lock
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-medium relative py-1 transition-colors duration-200 group ${isActive(link.path)
                    ? 'text-black dark:text-white font-bold'
                    : 'text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white'
                  }`}
              >
                {link.name}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-black dark:bg-white transform origin-left transition-transform duration-300 ${isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}></span>
              </Link>
            ))}

            {/* Theme Toggle */}
            <button
              onClick={handleToggleTheme}
              className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
              aria-label="Toggle Theme"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 transition-transform duration-300" />
              ) : (
                <Sun className="w-5 h-5 transition-transform duration-300" />
              )}
            </button>
          </div>

          {/* Mobile Menu Button & Theme Toggle */}
          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={handleToggleTheme}
              className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 transition-transform duration-300" />
              ) : (
                <Sun className="w-5 h-5 transition-transform duration-300" />
              )}
            </button>

            <button
              onClick={handleToggleMenu}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white focus:outline-none transition-transform duration-200 active:scale-95"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMenuOpen ? 'close' : 'open'}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </motion.div>
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="md:hidden absolute top-full left-0 w-full bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800 shadow-lg overflow-hidden"
          >
            <div className="flex flex-col py-4 px-4 space-y-2">
              {navLinks.map((link) => (
                <motion.div key={link.name} variants={itemVariants}>
                  <Link
                    to={link.path}
                    onClick={handleCloseMenu}
                    className={`block text-base font-medium px-4 py-3 rounded-xl transition-all ${isActive(link.path)
                        ? 'bg-black dark:bg-white text-white dark:text-black shadow-md'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-black dark:hover:text-white'
                      }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
});

export default Navbar;
